import { storageKey, readLocalJson, writeLocalJson, removeLocalKey, getDebugParams, resolveApiBaseUrl } from '../chunk-LDODPR3U.js';
export { ApiEndpointMode, DEBUG_QUERY_KEYS, getDebugParams, isDebugEnabled, parseDebugParams, resolveApiBaseUrl } from '../chunk-LDODPR3U.js';
import '../chunk-3Z32THMK.js';
import '../chunk-XR6TNFQL.js';
import '../chunk-HIWBYL4O.js';
import '../chunk-OCEENQIA.js';
import '../chunk-RMLZ5HCT.js';
import '../chunk-BRBMP7SU.js';
import '../chunk-MZH5OJVQ.js';
import '../chunk-5WIUKZF6.js';
import '../chunk-HCZGW64N.js';
import '../chunk-DCK4H5YA.js';
import '../chunk-PIHMOTST.js';
import '../chunk-BFUJOKOR.js';
import '../chunk-EE4CQDCZ.js';
import '../chunk-6B5KO6TH.js';
import '../chunk-3QN2VZ56.js';
import '../chunk-LNDNXOJP.js';
import '../chunk-LVCDLPYA.js';
import '../chunk-UEXNJL5R.js';
import '../chunk-AEYYQ7QM.js';
import '../chunk-B6XTEEPM.js';
import '../chunk-O2TUCS6S.js';
import '../chunk-RVVFBIJA.js';
import '../chunk-IMD7EVVL.js';
import '../chunk-X3O5L6NE.js';
import '../chunk-GPJ572YS.js';
import '../chunk-N7SZ66QO.js';
import '../chunk-LD6PO66H.js';
import '../chunk-AHISJLEK.js';
import '../chunk-QXYP3KHC.js';
import '../chunk-O5TCTRWM.js';
import '../chunk-O3OIBNP7.js';
import '../chunk-PZ5AY32C.js';

// src/config/qa.ts
var QA_SETTINGS_KEY = storageKey("config", "qa");
function getQaSettings() {
  return readLocalJson(QA_SETTINGS_KEY, {});
}
function setQaSettings(patch) {
  const next = { ...getQaSettings(), ...patch };
  writeLocalJson(QA_SETTINGS_KEY, next);
  return next;
}
function clearQaSettings() {
  removeLocalKey(QA_SETTINGS_KEY);
}
function hasQaOverrides() {
  const qa = getQaSettings();
  return Boolean(qa.endpointMode || qa.region || qa.flags && Object.keys(qa.flags).length);
}

// src/config/resource-paths.ts
function applyRule(path, rule) {
  if (typeof rule.match === "string") {
    if (!path.startsWith(rule.match)) return null;
    if (typeof rule.to === "function") return rule.to(path);
    return rule.to + path.slice(rule.match.length);
  }
  if (!rule.match.test(path)) return null;
  if (typeof rule.to === "function") return rule.to(path);
  return path.replace(rule.match, rule.to);
}
function rewriteResourcePath(path, config) {
  const qIndex = path.search(/[?#]/);
  const pathname = qIndex === -1 ? path : path.slice(0, qIndex);
  const suffix = qIndex === -1 ? "" : path.slice(qIndex);
  for (const rule of config.rules) {
    const rewritten = applyRule(pathname, rule);
    if (rewritten !== null) return rewritten + suffix;
  }
  return path;
}
function makePathRewriter(config) {
  return (path) => rewriteResourcePath(path, config);
}
var RESOURCE_REWRITES = {
  rules: [
    { match: /^\/pricing(?=\/|$)/, to: "/internal/token-mgmt-api/v0/pricing", note: "batched asynchronous pricing" },
    { match: /^\/tokens(?=\/|$)/, to: "/internal/token-mgmt-api/v0/tokens", note: "token definitions and live owners" },
    // The same service's NFT lenses (`/nfts/{chain}/{collection}/owners|sales`).
    // Without a rule the canonical path went to the gateway unrewritten, which
    // answers 403 with no CORS headers — so the browser reported "Failed to
    // fetch", the retry parked as offline, and a collection's Holders and Sales
    // cards span their skeletons forever with nothing said.
    { match: /^\/nfts(?=\/|$)/, to: "/internal/token-mgmt-api/v0/nfts", note: "NFT rows + live collection owners/sales" },
    // Blockchain transaction feed (overview): POST query + GET preview. →
    // https://{alias}.{continent}.api.{domain}/private/transactions/{blue/}{VERB}/{CONTINENT}/v0/blockchain
    { match: "/transactions/blockchain", to: "/private/transactions/v0/transactions/blockchain", note: "blockchain tx feed" },
    // Full transaction body (cold payload): 302-redirects to a short-lived
    // presigned S3 URL. Must come BEFORE the generic getOne rule (more specific).
    { match: /^\/transactions\/([^/]+)\/body$/, to: "/private/transactions/v0/transactions/$1/body", note: "tx full body (presigned redirect)" },
    // Transaction detail (canonical /transactions/:id → service-prefixed getOne).
    { match: /^\/transactions\/([^/]+)$/, to: "/private/transactions/v0/transactions/$1", note: "tx getOne" },
    // Transactions list.
    { match: "/transactions", to: "/private/transactions/v0/transactions", note: "tx list" },
    // Cross-chain transaction group view (served by transactions-mgmt-api). The
    // inverse of a tx's `cross_chain_groups`: resolve `/groups/:id` into the
    // group's member transactions + the merged cross-chain scene/highlights.
    // Mounts on the same `private/transactions` base as the tx routes; the
    // region router then injects {VERB}/{CONTINENT} (GET).
    { match: /^\/groups\/([^/]+)$/, to: "/private/transactions/v0/groups/$1", note: "cross-chain group view (members + merged scene)" },
    // Entities.
    { match: /^\/entities\/([^/]+)$/, to: "/private/entities/v0/entities/$1" },
    { match: "/entities", to: "/private/entities/v0/entities" },
    // Address clustering (related-addresses-mgmt, `internal` tier like proxy-mgmt).
    // One prefix rule covers the whole surface — `/clusters[/:id]`, `/cluster-runs
    // [/:id]` and the two per-address lookups (`/addresses/{chain}/{address}/
    // {cluster,links}`) — since it's a pure prefix swap onto the service's base
    // path. The region router then injects {VERB}/{CONTINENT} (GET for the reads,
    // ANY for the POST that requests a run).
    //
    // The canonical prefix is `/related-addresses`, NOT `/addresses/…/cluster`:
    // the generic `/addresses` prefix rule below is address-mgmt's and would claim
    // those two subpaths first (rules are first-win), silently sending a cluster
    // read to the wrong service.
    { match: "/related-addresses", to: "/internal/related-addresses-mgmt/v0", note: "address clusters, links + clustering runs (related-addresses-mgmt)" },
    // Addresses (address-mgmt-api). The single-segment rule covers the list-item
    // shape; the prefix rule below covers the multi-segment item + sub-resources
    // (`/addresses/{chain}/{address}/{activity|assignments|primary|share|transactions}`)
    // since it's a pure prefix swap. Both emit the standard
    // `/private/addresses/v0/...` shape, so the region router injects {VERB}/{CONTINENT}.
    // address-mgmt-api follows the verb-in-path `/{VERB}/${Continent}/v0/` convention
    // and mounts at `private/addresses` (was gaps ADDR1/ADDR2 in
    // `front-ends/docs/backend-prereqs.md`, both resolved), so this lines up as-is.
    { match: /^\/addresses\/([^/]+)$/, to: "/private/addresses/v0/addresses/$1" },
    { match: "/addresses", to: "/private/addresses/v0/addresses" },
    // Contract ABIs + on-chain state (contracts explorer). The contract-state
    // reader is a sibling resource at the abi-mgmt-api service root
    // (`/v0/contract-state`), so it must come BEFORE the generic `/abis/:id`
    // rule (which would otherwise rewrite it to `…/v0/abis/contract-state`).
    { match: "/abis/contract-state", to: "/private/abis/v0/contract-state", note: "on-chain contract state reader (get-contract-state)" },
    // Transaction-view enrichment (balance diff + identity). Sibling resource
    // at the abi-mgmt-api service root, like contract-state; must precede the
    // generic `/abis/:id` rule (which would mis-rewrite it).
    { match: "/abis/transaction-insight", to: "/private/abis/v0/transaction-insight", note: "transaction balance/identity enrichment (contract_state.enrich)" },
    // Per-contract ABI sub-resources (chain+address). These are MULTI-segment so
    // they must precede the single-segment `/abis/:id` rule. `by-address` lists
    // every ABI visible to the caller (own overrides + system/default catalog);
    // `…/refresh` force-refetches from third-party explorers; the bare
    // `{chain}/{address}` is getOne (GET) + update-on-edit (PATCH/PUT, which the
    // backend turns into copy-on-write for inherited/system rows).
    { match: /^\/abis\/by-address\/([^/]+)\/([^/]+)$/, to: "/private/abis/v0/abis/by-address/$1/$2", note: "ABIs visible for a contract (overrides + catalog)" },
    { match: /^\/abis\/([^/]+)\/([^/]+)\/refresh$/, to: "/private/abis/v0/abis/$1/$2/refresh", note: "force third-party ABI refetch" },
    { match: /^\/abis\/([^/]+)\/([^/]+)\/forensic$/, to: "/private/abis/v0/abis/$1/$2/forensic", note: "forensic read of one contract (+ its proxy implementations) for a transaction" },
    { match: /^\/abis\/([^/]+)\/([^/]+)$/, to: "/private/abis/v0/abis/$1/$2", note: "ABI getOne / update (chain+address)" },
    // Proxy resolution + override authoring + the upgrade timeline, all served by
    // proxy-mgmt on the `internal` tier. `/proxy-overrides/*` is the canonical
    // prefix (the rules are method-agnostic, so resolve/pin/unpin share one path).
    { match: /^\/proxy-overrides\/([^/]+)\/([^/]+)\/history$/, to: "/internal/proxy-mgmt/v0/proxy/$1/$2/history", note: "proxy upgrade timeline + the caller's pins" },
    { match: /^\/proxy-overrides\/([^/]+)\/([^/]+)$/, to: "/internal/proxy-mgmt/v0/proxy/$1/$2", note: "proxy override resolve (GET) / pin (PUT) / unpin (DELETE)" },
    // The service's two COLLECTION routes — `/resolve` (batch resolve, POST) and
    // `/index` (enqueue a Tier-3 build, POST). A prefix rule rather than two
    // literals because the two regexes above are the only other `/proxy-overrides`
    // rules and both need three segments, so anything reaching here is a
    // collection route and wants the same base swap. It has to be SOMETHING:
    // a single-segment tail matches neither regex, so `/proxy-overrides/index`
    // was falling through unrewritten and reaching a foreign catch-all — the
    // same header-less 404 the `/proxy/*` alias note below describes.
    { match: "/proxy-overrides", to: "/internal/proxy-mgmt/v0/proxy", note: "proxy collection routes: batch resolve (POST /resolve), index enqueue (POST /index)" },
    // `/proxy/*` is the older alias for the same read. It pointed at abi-mgmt-api,
    // which turned out never to have published the route (handler + swagger
    // fragment exist, wiring does not), so every call fell through to that API's
    // catch-all — a header-less 404 the browser reports as an opaque CORS error.
    // Aliased onto proxy-mgmt rather than deleted so an older bundle still reads.
    { match: /^\/proxy\/([^/]+)\/([^/]+)$/, to: "/internal/proxy-mgmt/v0/proxy/$1/$2", note: "resolve proxy implementations (alias of /proxy-overrides)" },
    // Verified contract source + optional bytecode/ABI (sibling resource at the
    // abi-mgmt-api root; `get-contract-source`, scope abis:show).
    { match: /^\/source\/([^/]+)\/([^/]+)$/, to: "/private/abis/v0/source/$1/$2", note: "verified contract source (+ bytecode/abi)" },
    { match: /^\/abis\/([^/]+)$/, to: "/private/abis/v0/abis/$1" },
    { match: "/abis", to: "/private/abis/v0/abis" },
    // Watches are standing interests in an address/entity/chain/tag, owned by
    // trigger-mgmt-api alongside the triggers they generate.
    { match: "/watches", to: "/private/trigger-mgmt-api/v0/watches", note: "standing watches (address/entity/chain/tag/situation) CRUD" },
    // The catalog a `situation` watch names, served by the same service so the
    // picker and the watch it authors cannot disagree about what exists. Read
    // only, and annotated with which chains each situation is actually covered
    // on — the field that stops a user authoring a watch that never fires.
    { match: "/situations", to: "/private/trigger-mgmt-api/v0/situations", note: "watchable on-chain situation catalog + coverage (read-only)" },
    // Notification inbox (notification-center-api). One prefix rule covers the
    // list (`/notifications`) AND the mutations (`/notifications/{read,read-all,
    // archive}`) since it's a pure prefix swap onto the `private/notifications`
    // base path. The region router then injects {VERB}/{CONTINENT} (GET for the
    // list, ANY for the POST mutations). Backend gap #2 in backend-prereqs.md.
    { match: "/notifications", to: "/private/notifications/v0/notifications", note: "in-app notification inbox (history + read/archive)" },
    // Teams + collaboration (teams-mgmt-api). One prefix rule covers the whole
    // surface — list/get (`/teams`, `/teams/:id`), members, roles, invites, audit,
    // `/teams/memberships`, and `/teams/invites/accept` — since it's a pure prefix
    // swap onto the `private/teams-mgmt-api` base path. The region router then
    // injects {VERB}/{CONTINENT} (GET for reads, ANY for the POST/PATCH/DELETE
    // writes). teams-mgmt-api mounts at `private/teams-mgmt-api` and publishes
    // routes under the `/{VERB}/${Continent}/v0/` segment, so this lines up as-is.
    { match: "/teams", to: "/private/teams-mgmt-api/v0/teams", note: "teams + collaboration (members/roles/invites/audit)" },
    // Stats overview (management-api). management-api is the one service mounted
    // on the `management` base path (its own `.management.` zone), NOT the
    // `private`/`public` tiers — so the rewrite's FIRST segment is `management`
    // (gateway base-path key) instead of `private/<service>`. The region router
    // sends `/management/` paths to `{alias}.{continent}.management.{domain}` and
    // injects {VERB}/{CONTINENT} after `/v0/`, so GET /stats/overview →
    // https://{alias}.{continent}.management.{domain}/management/{blue/}GET/{CONTINENT}/v0/stats/overview
    { match: "/stats/overview", to: "/management/v0/stats/overview", note: "dashboard stats overview (KPI tiles + activity series)" },
    // Metering reads (management-api, same `.management.` zone). One route serves
    // every usage surface — the billing page's live consumption, the runs page's
    // per-run panel (`?pipeline_run_id=`), the activity lane, a transaction's own
    // cost (`?tx_id=`) and the admin portal's breakdown (`?group_by=tenant`). The
    // caller's `customer_id` is never on the wire: the backend takes it from the
    // verified auth context, so this path cannot be pointed at another customer.
    { match: "/usage", to: "/management/v0/usage", note: "metering reads (usage events + rollup + breakdown) \u2014 management-api" },
    // The tenancy's raw call trail (management-api `get-audit`, same zone). The
    // OTHER half of the Activity page: the notification inbox is what a service
    // chose to announce, this is every invocation the platform recorded. Like
    // `/usage`, the partition is taken from the verified auth context and is not
    // addressable from the wire.
    { match: "/audit", to: "/management/v0/audit", note: "the tenancy's own call trail (Activity \u2192 Audit) \u2014 management-api" },
    // Customer feature flags (management-api `get-features`, same `.management.`
    // zone as `/settings`). The dashboard has no client-config access; management
    // relays client-config-api's resolution for the caller's OWN tenancy, taken
    // from the verified auth context (no ids on the wire). Anchored so a
    // `/features-*` path never lands here.
    { match: /^\/features(?=\/|$)/, to: "/management/v0/features", note: "resolved customer feature flags \u2014 management-api" },
    // Enforced visibility policy (management-api `get-policies`, same zone and
    // the same reason: a relay of client-config-api for the caller's own
    // tenancy). Anchored like `/features` so no `/policies-*` path lands here.
    { match: /^\/policies(?=\/|$)/, to: "/management/v0/policies", note: "enforced visibility policy \u2014 management-api" },
    // Tenancy admin (management-api, same `.management.` zone as stats). One
    // prefix rule per top-level resource covers the whole sub-tree since it's a
    // pure prefix swap: `/organizations` also serves the nested tenants
    // (`/organizations/{id}/tenants[/{tenant_id}]`) and `/customers` serves
    // `/customers/{id}`. The region router then injects {VERB}/{CONTINENT} (GET
    // for list/get, ANY for create/rename/suspend). Backed by get/update/delete
    // -organization, get/create/update-tenant and get/update/delete-customer.
    { match: "/organizations", to: "/management/v0/organizations", note: "organizations + nested tenants (list/get/rename/create) \u2014 management-api" },
    { match: "/customers", to: "/management/v0/customers", note: "customer read / rename / suspend \u2014 management-api" },
    // API keys (management-api, same `.management.` zone). A prefix rule so
    // `/api-keys/{id}` (the revoke) rides the same swap as the list + create.
    // The dashboard manages the caller's OWN keys and the workspace's shared
    // ones; naming another tenancy is an admin-portal-only request the backend
    // refuses here (`management_shared.api_key_target`).
    { match: "/api-keys", to: "/management/v0/api-keys", note: "API keys the caller can manage (list/mint/revoke) \u2014 management-api" },
    // Catalog registry (catalog-mgmt): entities/tags/labels/groups CRUD plus the
    // assignment + primary-selection overlays. catalog-mgmt is the JWT-authorized
    // EXTERNAL/customer catalog API, so it mounts at the base path
    // `private/catalog-mgmt` (`api_application = "private"` + `api_service_path =
    // var.project`), the same tier as the sibling address-mgmt-api
    // (`private/addresses`). It exposes everything under `/v0/...`:
    //   GET/POST          /v0/catalog/{resource}          (+ /{id} for getOne/PATCH/PUT/DELETE)
    //   GET/POST/DELETE   /v0/catalog-assignments
    //   GET/POST/PUT/DELETE /v0/catalog-primary
    // The overlay resources are deliberately distinct top-level paths (not
    // `/catalog/assignments`) so a `{resource}` path-var never competes with a
    // static sibling — keep them BEFORE the generic `/catalog` rule. The region
    // router then injects {VERB}/{CONTINENT} (GET for reads, ANY for writes).
    // (The internal/admin variant catalog-mgmt-internal lives on the `internal`
    // tier and is consumed by admin-portal, not by these customer rewrites.)
    { match: "/catalog-assignments", to: "/private/catalog-mgmt/v0/catalog-assignments", note: "catalog assignment overlay (list/assign/unassign for a scope target)" },
    { match: "/catalog-primary", to: "/private/catalog-mgmt/v0/catalog-primary", note: "catalog primary selection (resolve/set/clear)" },
    // Presigned logo/asset upload ticket (catalog-mgmt `assets_upload_ticket`
    // lambda): POST returns a presigned PUT so the browser uploads a logo
    // straight to the shared CDN bucket. Same `private/catalog-mgmt` base path as
    // the catalog rows above; the region router injects {VERB}/{CONTINENT} (ANY),
    // so it resolves to `/private/catalog-mgmt/{colour/}ANY/{CONTINENT}/v0/assets/upload-ticket`
    // — matching the gateway route in catalog-mgmt/infra/modules/assets/swagger.yml.
    // Keep BEFORE the generic `/catalog` rule isn't required (distinct prefix),
    // but group it with catalog since it's the same service.
    { match: "/assets/upload-ticket", to: "/private/catalog-mgmt/v0/assets/upload-ticket", note: "presigned logo upload ticket (catalog-mgmt)" },
    { match: "/catalog", to: "/private/catalog-mgmt/v0/catalog", note: "catalog rows (entities/tags/labels/groups CRUD)" },
    // CloudFront signed-cookie mint (authentication service). The region router
    // injects `ANY` for the POST, and the authentication service publishes the
    // route under the `/ANY/${Continent}/v0/` segment + maps `private/authentication`
    // on the regional `.api.` host, so `/private/authentication/...` routes as-is.
    { match: "/mint-cdn-cookies", to: "/private/authentication/v0/mint-cdn-cookies", note: "CloudFront signed-cookie mint" },
    // Device root-key enrolment (authentication service) for ECDSA two-key
    // request signing. Same authentication-gateway routing as mint-cdn-cookies:
    // the region router injects `ANY` for the POST and maps `private/authentication`
    // on the regional `.api.` host.
    { match: "/device-keys", to: "/private/authentication/v0/device-keys", note: "device root-key enrolment (two-key signing)" },
    // Share links (saved-views-api). The PUBLIC resolve `GET /shares/{token}`
    // is unauthenticated but still mounts on the same `private/saved-views-api`
    // base path (the route itself carries no authorizer); the owner mutations
    // `PATCH/DELETE /shares/{token}` go through the authorizer. One prefix rule
    // covers both since it's a pure prefix swap; keep it BEFORE `/saved-views`
    // so it isn't shadowed. The region router injects {VERB}/{CONTINENT}.
    { match: "/shares", to: "/private/saved-views-api/v0/shares", note: "saved-view share links (public resolve + owner revoke/update)" },
    // Saved views (saved-views-api). One prefix rule covers the whole surface —
    // list/create (`/saved-views`), getOne/update/delete (`/saved-views/:id`),
    // and the per-view share collection (`/saved-views/:id/shares`) — since it's
    // a pure prefix swap onto the `private/saved-views-api` base path. The region
    // router injects {VERB}/{CONTINENT} (GET reads, ANY writes). saved-views-api
    // mounts at `private/saved-views-api` and publishes routes under the
    // `/{VERB}/${Continent}/v0/` segment, so this lines up as-is.
    { match: "/saved-views", to: "/private/saved-views-api/v0/saved-views", note: "generic saved views (table/graph/replay) CRUD + per-view shares" },
    // The export ledger — the same service, a sibling resource at its root
    // (`GET /exports`, `POST /exports`, `GET|PATCH|DELETE /exports/{id}`), all
    // of which `saved-views-api` publishes and none of which a rule reached.
    //
    // The consequence was the whole audit surface, not one page: `@authe/export`
    // lists from `/exports` and RECORDS every download to it, and `record()`
    // deliberately swallows its own errors so a failed audit write can never
    // take a download down. So exports were never recorded, silently, and
    // Settings → Exports answered "An upstream provider is unavailable" for a
    // request that never left the browser — the same shape as the `/feedback`
    // bug recorded further down this file.
    { match: "/exports", to: "/private/saved-views-api/v0/exports", note: "export ledger: what left the product, by whom, and any stored copy" },
    // Investigations (investigation-mgmt-api) — collaborative case files. One
    // prefix rule covers the whole surface, since it is a pure prefix swap:
    // list/create (`/investigations`), the case itself (`/investigations/:id`),
    // its derived hand-off scope and annotation promotion, and the four
    // collections under it (`/evidence`, `/comments`, `/workflow`, `/collab`).
    //
    // Cases used to be saved views at `tableId: "investigations"`, which is why
    // this rule sits directly beneath that one — it is the surface that moved
    // out. The `/evidence/references/...` reverse lookup is a sibling resource at
    // the service root rather than under a case, so it needs its own rule and
    // must come FIRST or the `/investigations` prefix would never see it.
    { match: "/evidence/references", to: "/private/investigation-mgmt-api/v0/evidence/references", note: "which investigations pinned this reference" },
    // The Ask AI assistant (ask-ai-api), mounted at `private/ai` with its routes
    // under `/v0/ai/…`: POST /ai/chat starts a turn, GET|DELETE
    // /ai/threads/{thread}/turns/{turn} polls or stops it, /ai/threads lists and
    // deletes conversations. A regex on the segment boundary, so a future
    // `/aiXyz` resource is never sent here.
    { match: /^\/ai(?=\/|$)/, to: "/private/ai/v0/ai", note: "Ask AI assistant: turns + conversations" },
    { match: "/investigations", to: "/private/investigation-mgmt-api/v0/investigations", note: "collaborative investigation case files (was: saved views)" },
    // Subject-scoped graph overlays, case-free. Same service and same table as
    // `promote` writes to — an annotation is about an account or a transaction,
    // and a case is one place a person happens to be standing when they make one.
    { match: "/graph-overlays", to: "/private/investigation-mgmt-api/v0/graph-overlays", note: "annotations keyed to a subject rather than a case" },
    // A team's discussion of a transaction: the same collaboration log as a
    // case's `/collab`, gated on membership of the TEAM. Its own resource rather
    // than a path under `/teams/…`, because `/teams` is teams-mgmt-api's prefix
    // below and a longer path beneath it would be sent to the wrong service.
    { match: "/team-discussions", to: "/private/investigation-mgmt-api/v0/team-discussions", note: "durable team discussion of a transaction (collaboration log)" },
    // Transaction pipeline platform (pipeline-mgmt-api). Mounts at
    // `private/pipeline-mgmt-api` and publishes everything under `/v0/...` with
    // greedy `{proxy+}` tails, so each canonical resource is a pure prefix swap
    // (the region router then injects {VERB}/{CONTINENT}: GET reads, ANY writes).
    //   GET/POST          /v0/pipelines            (+ /{id}, /catalog, /validate, /dry-run)
    //   POST              /v0/process              (single-tx local run)
    //   GET/POST          /v0/replays              (+ /{id})
    //   GET/POST          /v0/runs                 (+ /{id})  forward-ingest jobs
    //   GET               /v0/pipeline-runs/{id}   (+ ?include_steps) one execution
    //   GET               /v0/bodies/{canonical_tx_id}
    //   GET/POST/PATCH/DELETE /v0/tracers          (+ /{id}, /{id}/runs, /{id}/run)
    // Keep these specific resource prefixes ordered before nothing that shadows
    // them (no sibling shares this base today).
    // Before `/pipelines`, which is a PREFIX rule: a first-win table must not
    // let the shorter resource claim this one if either name ever grows a
    // shared prefix. One EXECUTION (one transaction's pass through the DAG),
    // not the run job above — `/runs/{job}` fans out into many of these.
    { match: "/quality-runs", to: "/private/pipeline-mgmt-api/v0/quality-runs", note: "decoding quality assessments and replay comparisons" },
    { match: "/pipeline-runs", to: "/private/pipeline-mgmt-api/v0/pipeline-runs", note: "per-transaction executions (+ ?include_steps for the per-step outcomes)" },
    { match: "/pipelines", to: "/private/pipeline-mgmt-api/v0/pipelines", note: "pipeline definitions + catalog/validate/dry-run (proxy tail)" },
    { match: "/process", to: "/private/pipeline-mgmt-api/v0/process", note: "synchronous single-transaction pipeline run" },
    { match: "/replays", to: "/private/pipeline-mgmt-api/v0/replays", note: "replay jobs (list/create/get; worker re-runs async)" },
    { match: "/runs", to: "/private/pipeline-mgmt-api/v0/runs", note: "forward-ingest run jobs (list/create/get; worker ingests + fans out async)" },
    { match: "/bodies", to: "/private/pipeline-mgmt-api/v0/bodies", note: "processed transaction bodies (presigned GET / inline JSON)" },
    { match: "/tracers", to: "/private/pipeline-mgmt-api/v0/tracers", note: "money-flow tracers + run snapshots (proxy tail)" },
    // tx_grouping's read + authoring surface. One prefix rule covers all three
    // sub-resources (`/grouping/{rules,groups,memberships}`) since the service
    // sub-routes them off a greedy `{proxy+}` tail. `groups` returns the
    // platform's own SYSTEM-scoped detections (a rug, a sandwich, a pool's
    // lifecycle) merged with the tenant's own groups.
    { match: "/grouping", to: "/private/pipeline-mgmt-api/v0/grouping", note: "grouping rules + transaction groups + tx<->group memberships" },
    // Triggers/watches/alerts (trigger-mgmt-api). The FE/alerts package speaks
    // canonical `/alert-*` paths; the service exposes one top-level collection
    // per resource (`/v0/{triggers,watches,destinations,alerts}` with a greedy
    // `{proxy+}` tail), so each canonical collection is a pure prefix swap.
    { match: "/alert-rules", to: "/private/trigger-mgmt-api/v0/triggers", note: "alert/trigger rules CRUD" },
    { match: "/alert-destinations", to: "/private/trigger-mgmt-api/v0/destinations", note: "alert delivery destinations CRUD" },
    { match: "/alert-history", to: "/private/trigger-mgmt-api/v0/alerts", note: "fired alert/trigger events feed" },
    // Customer-authored integrations (integrations-mgmt-api). The provisioning
    // lifecycle the code editor drives: register -> version + presigned upload
    // -> build -> promote, plus the grants that bound what the published code
    // may see. Same greedy `{proxy+}` sub-routing as the trigger service, so
    // each canonical collection is a pure prefix swap.
    //
    // These are what makes the integrations API reachable AT ALL from a front
    // end: without them a caller has to hand-build the host and the
    // `{VERB}/{CONTINENT}` segments, and gets all three wrong (the zone is
    // `.api.` not `.private.`, and the verb is GET or ANY — never the literal
    // POST/PATCH/DELETE).
    { match: "/integrations", to: "/private/integrations-mgmt-api/v0/integrations", note: "customer-authored integration definitions CRUD + promote" },
    { match: "/integration-versions", to: "/private/integrations-mgmt-api/v0/versions", note: "integration source versions: presigned upload + build" },
    { match: "/integration-grants", to: "/private/integrations-mgmt-api/v0/grants", note: "what a published integration is allowed to see" },
    // Amazon Connect live-chat start (aws-connect-mgmt). The "discuss your needs"
    // contact route is PUBLIC + UNAUTHENTICATED, so it mounts on the `public`
    // tier base path (`public/aws-connect-mgmt`) — the region router still injects
    // {VERB}/{CONTINENT} (ANY for the POST) and resolves the regional `.api.`
    // host, so POST /connect/chat →
    // https://{alias}.{continent}.api.{domain}/public/aws-connect-mgmt/{blue/}ANY/{CONTINENT}/v0/connect/chat
    { match: "/connect/chat", to: "/public/aws-connect-mgmt/v0/connect/chat", note: "public Amazon Connect live-chat start (aws-connect-mgmt, unauthenticated)" },
    // Metadata registry (metadata-registry-api). The SSOT for decoded-blockchain
    // semantics: analytics reads over the EAV/materialized-views, the facet/view/
    // path vocabulary CRUD, and the per-tenancy confidence-floor policy. Unlike
    // the customer-tier services above, metadata-registry-api mounts on the
    // `internal` tier (`api_application = "internal"` + `api_service_path =
    // var.project`), so its base path KEEPS the `internal/<service>` prefix and
    // the region router swaps the host zone to `.internal.` (or the `.api.` zone
    // exposes the same gateway). The dashboard's external token reaches these
    // reads via the scope grant added in auth0-mgmt (RBAC gates the verb; the
    // handler still scopes every query by the caller's tenancy). One prefix rule
    // per top-level resource — each is a pure prefix swap onto `/v0/...`, so the
    // region router injects {VERB}/{CONTINENT} (GET reads, ANY writes).
    //   GET/ANY  /v0/analytics/{metadata,views/:id,timeseries}   (reads)
    //   GET/ANY  /v0/facets[/:name]  /v0/views[/:id|/plan]  /v0/paths[/:id]
    //   GET/ANY  /v0/policy                                       (confidence floor)
    //   GET/ANY  /v0/protocols[/:id]                              (definitions + overrides)
    //   ANY      /v0/resolve                                      (normalize preview)
    // Keep `/metadata-analytics` (covers the nested `/views/:id` + `/timeseries`)
    // before the generic collection rules — distinct prefixes, but grouped here.
    { match: "/metadata-analytics", to: "/internal/metadata-registry-api/v0/analytics", note: "metadata-registry analytics reads (EAV + materialized views + timeseries)" },
    { match: "/metadata-facets", to: "/internal/metadata-registry-api/v0/facets", note: "metadata-registry facet definitions (typed, filterable columns)" },
    { match: "/metadata-views", to: "/internal/metadata-registry-api/v0/views", note: "metadata-registry materialized-view specs (+ /plan preview)" },
    { match: "/metadata-paths", to: "/internal/metadata-registry-api/v0/paths", note: "metadata-registry path vocabulary (raw wire name -> canonical fact)" },
    { match: "/metadata-policy", to: "/internal/metadata-registry-api/v0/policy", note: "metadata-registry per-tenancy policy (confidence-floor ladder)" },
    { match: "/metadata-protocols", to: "/internal/metadata-registry-api/v0/protocols", note: "metadata-registry protocol definitions + per-scope definition_patch overrides" },
    // Longest-match first: `/evidence-policy/preview` must not be swallowed by
    // the `/evidence-policy` rule, or a dry run would post to the save route.
    { match: "/metadata-evidence-policy/preview", to: "/internal/metadata-registry-api/v0/evidence-policy/preview", note: "metadata-registry confidence-policy preview (never writes)" },
    { match: "/metadata-evidence-policy", to: "/internal/metadata-registry-api/v0/evidence-policy", note: "metadata-registry per-scope confidence policy (floors + gate actions, whole ladder + provenance)" },
    { match: "/metadata-dashboards", to: "/internal/metadata-registry-api/v0/dashboards", note: "metadata-registry saved analytics dashboards (CRUD, scope-resolved)" },
    { match: "/metadata-resolve", to: "/internal/metadata-registry-api/v0/resolve", note: "metadata-registry semantic-normalization preview" },
    // The EFFECTIVE chain registry (bundled catalogue ⊕ the published
    // `manual:chain_registry`), as resolved rows. Read in the background from
    // bootstrap on (and every 10 minutes) by `startEffectiveChainsHydration`, so
    // a chain added or switched off in the admin portal reaches every picker and
    // lookup without a rebuild.
    { match: "/metadata-chains", to: "/internal/metadata-registry-api/v0/chains", note: "metadata-registry effective chain registry (resolved rows)" },
    // The human review queue (seed-mgmt-api). Every "needs a human" producer in
    // the platform — a seed run, the pipeline's catalog flush, a third-party
    // reconciler, an ML prediction — enqueues onto ONE queue, and this is the
    // only surface that lists and decides it. The whole reason the queue is
    // worth a UI: a quarantined discovery that nobody approves is a catalog row
    // that never exists, and until now nothing outside the CLI could approve one.
    //
    // seed-mgmt-api mounts on the `internal` tier (`api_application = "internal"`
    // + `api_service_path = var.project`), like metadata-registry-api, so the base
    // path KEEPS its `internal/<service>` prefix and the region router swaps the
    // host zone. The routes live under the service's own `/seed/` namespace
    // (`/v0/seed/reviews[/{review_id}]`), which is why the canonical FE prefix is
    // `/seed-reviews` and not `/reviews`: one prefix rule is a pure swap covering
    // the list (GET), the bulk decide (PATCH), the item read (GET) and the item
    // decide / edit / delete (PATCH / DELETE). The region router then injects
    // {VERB}/{CONTINENT} — GET for the reads, ANY for the decisions.
    //
    // Access: the authorizer enforces the swagger's `scope:seed:read` (reads),
    // `scope:seed:review` (edits + decisions) and `scope:seed:admin` (the
    // permanent delete). `scopes/dashboard.json` mints read + review, and the
    // dashboard's gateway whitelist includes seed-mgmt-api. For a dashboard
    // (external) caller every review route is TENANT-SCOPED server-side: the list
    // holds only the caller's org-tenant (and their own user-scoped) candidates, a
    // foreign item reads as 404 and refuses a decision with 403. Platform-wide
    // candidates, seed runs and provider config stay operator-only (admin portal).
    { match: "/seed-reviews", to: "/internal/seed-mgmt-api/v0/seed/reviews", note: "human review queue \u2014 list / get / decide / bulk-decide / delete (seed-mgmt-api)" },
    // Research requests (seed-mgmt-api): a person asking who a set of addresses
    // are — the "Identify addresses" action on a case. POST asks
    // (`scope:seed:create`, Writer-tier), GET reads one request or a case's
    // requests (`?context=investigation:inv_…`, `scope:seed:read`). Tenant-scoped
    // server-side like the review queue; seed RUNS stay operator-only.
    { match: "/seed-research", to: "/internal/seed-mgmt-api/v0/seed/research", note: "address research requests \u2014 ask / read one / a case's (seed-mgmt-api)" },
    // Global search aggregator (search-mgmt-api). Mounts at `private/search`
    // (api_application = "private", api_service_path = "search") and serves both
    // `GET /v0/search` (typeahead) and `POST /v0/search` (full query body) — one
    // prefix rule covers both since it's a pure prefix swap. The region router
    // then injects {VERB}/{CONTINENT} (GET for the typeahead, ANY for the POST).
    { match: "/search", to: "/private/search/v0/search", note: "global search (tx/address/catalog/tenancy aggregator)" },
    // In-product feedback + NPS (nps-mgmt). Mounts at `private/nps-mgmt`
    // (api_application = "private", api_service_path = var.project). The submit
    // route is a POST, so the region router injects ANY; the two admin reads are
    // GETs and get GET. Order matters here: `/admin/feedback/aggregate` is a
    // PREFIX of nothing else, but it must precede `/admin/feedback`, which would
    // otherwise swallow it (rules are first-win prefix matches).
    //
    // Until these rules existed the `@authe/feedback-nps` package posted to a
    // bare `/feedback`, which no rule rewrote and no gateway served — it reached
    // the CloudFront apex, hit the catch-all 404, and every submission from the
    // setup wizard was swallowed by the caller's non-fatal try/catch.
    { match: "/admin/feedback/aggregate", to: "/private/nps-mgmt/v0/admin/feedback/aggregate", note: "NPS + sentiment rollup over a window (nps-mgmt)" },
    { match: "/admin/feedback", to: "/private/nps-mgmt/v0/admin/feedback", note: "admin feedback feed (nps-mgmt)" },
    { match: "/feedback", to: "/private/nps-mgmt/v0/feedback", note: "submit one feedback/NPS response (nps-mgmt)" }
  ]
};

// src/config/index.ts
function resolveApiRuntime(config, ctx = {}) {
  const qa = ctx.qa ?? getQaSettings();
  const debug = ctx.debug ?? getDebugParams(ctx.search);
  const endpointMode = debug.endpointMode ?? qa.endpointMode ?? config.defaultMode ?? "cloudfront";
  return {
    baseUrl: resolveApiBaseUrl(config, endpointMode),
    endpointMode,
    region: debug.region ?? qa.region,
    debug: debug.verbose === true
  };
}

export { QA_SETTINGS_KEY, RESOURCE_REWRITES, clearQaSettings, getQaSettings, hasQaOverrides, makePathRewriter, resolveApiRuntime, rewriteResourcePath, setQaSettings };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map